import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    HostBinding,
    inject,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { IconComponent } from '../../../icon/icon.component';
import { SanitizeHtmlPipe } from '../../pipes/sanitize-html.pipe';
import { TintDirective } from '../tint/tint.directive';
import { KeyTopicOneConfiguration } from './models/key-topic-one-block.types';
import { VideoComponent } from '../video-block/components/video/video.component';

@Component({
    selector: 'nw-key-topic-one-block',
    imports: [
        SanitizeHtmlPipe,
        TintDirective,
        AsyncPipe,
        IconComponent,
        NgTemplateOutlet,
        NgOptimizedImage,
        VideoComponent
    ],
    templateUrl: './key-topic-one-block.component.html',
    styleUrl: './key-topic-one-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KeyTopicOneBlockComponent implements OnChanges {
    @Input() config!: KeyTopicOneConfiguration | string;
    @Input({ transform: booleanAttribute }) centered: boolean = false;

    @HostBinding('class.nw-key-topic-one-block--light-bg')
    lightBackground: boolean = false;
    @HostBinding('class.nw-key-topic-one-block--dark-bg')
    darkBackground: boolean = false;
    @HostBinding('class.nw-key-topic-one-block--black-bg')
    blackBackground: boolean = false;
    @HostBinding('class.nw-key-topic-one-block--blue-bg')
    blueBackground: boolean = false;
    @HostBinding('class.nw-key-topic-one-block--white-text') whiteText: boolean = false;

    private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
    private document: Document = inject(DOCUMENT);

    public Ceil = (x: number): number => Math.ceil(x);
    public Max = (x: number[]): number => Math.max(...x);
    public configuration!: KeyTopicOneConfiguration;
    public maxGridSize?: number;
    public maxRow?: number;
    public gridSize$: Observable<boolean> = this.breakpointObserver
        .observe('(min-width: 768px)')
        .pipe(
            map(({ matches }) => !matches),
            tap((matches) => {
                const maxBy = <T>(
                    arr: T[],
                    func: (value: T) => number
                ): T | undefined => {
                    const max = Math.max(...arr.map(func));
                    return arr.find((item) => func(item) === max);
                };

                const MAX_COLUMN_END: number | undefined =
                    maxBy<KeyTopicOneConfiguration.Content>(
                        this.configuration.content,
                        ({ columnEnd }: KeyTopicOneConfiguration.Content) =>
                            columnEnd || 0
                    )?.columnEnd || undefined;

                this.maxRow =
                    maxBy<KeyTopicOneConfiguration.Content>(
                        this.configuration.content,
                        ({ rowEnd }: KeyTopicOneConfiguration.Content) => rowEnd || 0
                    )?.rowEnd || this.configuration?.content?.length;

                if (matches) {
                    if (this.configuration.content.length <= 3)
                        return (this.maxGridSize = 1);

                    return (this.maxGridSize = 2);
                } else {
                    if (MAX_COLUMN_END === 1) return (this.maxGridSize = 1);
                    if (MAX_COLUMN_END === 2) return (this.maxGridSize = 2);
                    if (MAX_COLUMN_END === 3) return (this.maxGridSize = 3);
                    if (MAX_COLUMN_END === 4) return (this.maxGridSize = 4);
                    if (MAX_COLUMN_END && MAX_COLUMN_END >= 5) {
                        if (this.configuration.content.length % 4) {
                            return (this.maxGridSize = 4);
                        }

                        return (this.maxGridSize = 4);
                    } else {
                        if (this.configuration.content.length % 4 === 0)
                            return (this.maxGridSize = 4);
                        if (this.configuration.content.length % 3 === 0)
                            return (this.maxGridSize = 3);
                        if (this.configuration.content.length % 2 === 0)
                            return (this.maxGridSize = 2);

                        return (this.maxGridSize = 4);
                    }
                }
            })
        );
    public containsMedia!: boolean;

    public ngOnChanges({ config }: SimpleChanges): void {
        if (config?.currentValue) {
            if (typeof this.config === 'string') {
                this.configuration = JSON.parse(this.config);
            } else {
                this.configuration = { ...this.config };
            }
            this.lightBackground = this.configuration.background === 'light';
            this.darkBackground = this.configuration.background === 'dark';
            this.blackBackground = this.configuration.background === 'black';
            this.blueBackground = this.configuration.background === 'blue';
            this.whiteText = this.configuration.color === 'white';

            const mediaItems = this.configuration.content.filter(
                ({ image, video }) => !!image || !!video
            );
            const nonFullHeightMedia = this.configuration.content.filter(
                ({ image, video }) => !!video || (!!image && image.height !== '100%')
            );
            this.containsMedia =
                mediaItems.length !== 0 &&
                mediaItems.length === nonFullHeightMedia.length;
        }
    }

    private get lightbox(): HTMLElement {
        return this.document.querySelector(
            `.nw-key-topic-one-block__container__col__img__lightbox`
        )!;
    }

    public showLightbox(image: KeyTopicOneConfiguration.Image, tintId: string): void {
        if (image?.link) {
            location.assign(image.link);
            return;
        }

        if (!image.lightbox) return;

        this.lightbox.querySelector('img')!.src = image.path;
        this.lightbox.querySelector('img')!.alt = image.alt || 'Lightbox image';
        this.lightbox.querySelector('img')!.style.filter = `url(#${tintId})`;
        this.document.querySelector('body')!.classList.add('overflow-hidden-important');

        this.lightbox.style.opacity = '1';
        this.lightbox.style.pointerEvents = 'all';

        this.reflowAnimation();
    }

    public hideLightbox(): void {
        this.lightbox.style.opacity = '0';
        this.lightbox.style.pointerEvents = 'none';

        this.document
            .querySelector('body')!
            .classList.remove('overflow-hidden-important');

        this.reflowAnimation();

        setTimeout(() => {
            this.lightbox.querySelector('img')!.src = '';
            this.lightbox.querySelector('img')!.alt = '';
        }, 250);
    }

    private reflowAnimation(): void {
        this.lightbox.style.animation = 'none';
        this.lightbox.offsetHeight; /* trigger reflow */
        // @ts-expect-error - TS doesn't know that we're setting the animation to null
        this.lightbox.style.animation = null;
    }
}
